import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import * as ep from '../api/endpoints';
import type { ReportJob } from '../api/types';
import { useMe } from '../app/auth';
import { PeriodPicker, usePeriods } from '../components/PeriodPicker';
import { ErrorBox, Field, Loading, PeriodBadge } from '../components/ui';
import { formatDateTime, yearMonthLabel } from '../lib/dates';
import { messageOf } from '../lib/errors';
import { printPdf, sharePdf } from '../platform/print';

/**
 * Arşiv / raporlar (PDF §04, §14): ay seçici, açık/kapalı durumu, kişi veya ortak rapor,
 * fotoğraf ekleme seçeneği, yazdırma. Her kullanıcı kendi ve tüm kullanıcıların raporunu alabilir (K10).
 */
export function ArchiveScreen() {
  const me = useMe();
  const qc = useQueryClient();
  const periods = usePeriods();
  const [periodId, setPeriodId] = useState<string | undefined>();
  const pid = periodId ?? periods.data?.[0]?.id;
  const period = periods.data?.find((p) => p.id === pid);
  const members = useQuery({ queryKey: ['members'], queryFn: ep.members });
  const [scope, setScope] = useState<'SELF' | 'PERSON' | 'ALL'>('ALL');
  const [ownerId, setOwnerId] = useState('');
  const [photos, setPhotos] = useState(false);
  const jobs = useQuery({
    queryKey: ['reports'], queryFn: ep.reports,
    refetchInterval: (q) => (q.state.data?.some((j) => j.status === 'QUEUED' || j.status === 'RUNNING') ? 2000 : false),
  });
  const create = useMutation({
    mutationFn: () => ep.createReport({ period_id: pid!, scope, owner_id: scope === 'PERSON' ? ownerId : undefined, include_photos: photos }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });

  return (
    <div className="stack">
      <h1>Arşiv ve raporlar</h1>
      <div className="card stack">
        <div className="form-grid two">
          <Field label="Dönem"><PeriodPicker value={pid} onChange={setPeriodId} /></Field>
          <Field label="Kapsam">
            <select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
              <option value="ALL">Tüm kullanıcılar (ortak rapor)</option>
              <option value="SELF">Yalnızca ben ({me.user.full_name})</option>
              <option value="PERSON">Belirli bir kişi</option>
            </select>
          </Field>
          {scope === 'PERSON' && (
            <Field label="Kişi">
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} required>
                <option value="">Seçin</option>
                {members.data?.map((m) => <option key={m.user_id} value={m.user_id}>{m.full_name}</option>)}
              </select>
            </Field>
          )}
          <label className="row" style={{ alignSelf: 'end' }}>
            <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={photos} onChange={(e) => setPhotos(e.target.checked)} />
            Fiş fotoğraflarını ekle
          </label>
        </div>
        {period && (
          <div className="row small">
            <strong>{yearMonthLabel(period.year_month)}</strong> <PeriodBadge status={period.status} />
            <span className="muted">{period.status === 'CLOSED' ? `Kapanış: ${formatDateTime(period.closed_at)} — rakamlar kapanış anındaki snapshot'tan.` : 'Dönem henüz kapatılmadı; rapor istek anının verisini içerir.'}</span>
          </div>
        )}
        <ErrorBox error={create.error} />
        <button className="btn primary" disabled={!pid || create.isPending || (scope === 'PERSON' && !ownerId)} onClick={() => create.mutate()}>Rapor oluştur</button>
      </div>

      <h2>Raporlarım</h2>
      {!jobs.data ? <Loading /> : (
        <div className="card">
          <ul className="list">
            {jobs.data.map((j) => <ReportRow key={j.id} job={j} />)}
            {!jobs.data.length && <li className="empty">Henüz rapor yok.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function ReportRow({ job }: { job: ReportJob }) {
  const [err, setErr] = useState<string | null>(null);
  async function withUrl(fn: (url: string) => Promise<void>) {
    setErr(null);
    try {
      const fresh = await ep.report(job.id); // imzalı bağlantı 60 sn geçerli: her seferinde tazele
      if (!fresh.download_url) throw new Error('Rapor henüz hazır değil.');
      await fn(fresh.download_url);
    } catch (e) { setErr(messageOf(e)); }
  }
  const label = { SELF: 'Kişisel', PERSON: 'Kişi', ALL: 'Ortak' }[job.scope];
  const name = `AKSAN-${job.year_month}-${label}`;
  return (
    <li>
      <div className="item">
        <div style={{ flex: 1 }}>
          <div className="title">{yearMonthLabel(job.year_month)} · {label} rapor{job.include_photos ? ' · fotoğraflı' : ''}</div>
          <div className="small muted">Veri kesimi {formatDateTime(job.as_of)} · istek {formatDateTime(job.created_at)}</div>
          {err && <div className="small neg">{err}</div>}
        </div>
        {job.status === 'DONE' ? (
          <>
            <button className="btn small" onClick={() => void withUrl((u) => printPdf(u, name))}>Yazdır</button>
            <button className="btn small" onClick={() => void withUrl((u) => sharePdf(u, name))}>Paylaş / kaydet</button>
          </>
        ) : job.status === 'FAILED' ? <span className="badge bad">Başarısız</span> : <span className="badge info">Hazırlanıyor</span>}
      </div>
    </li>
  );
}
